/**
 * 模块级语言缓存：从接口获取并缓存，供 getLanguageName 兜底使用
 * 模块加载时立即预取，后续同步读取缓存
 */
import { getApiOrigin } from '../config/apiBase';

let languageCache: Record<string, string> | null = null;
let languageCachePromise: Promise<Record<string, string>> | null = null;

const fetchLanguageCache = (): Promise<Record<string, string>> => {
  if (languageCachePromise) return languageCachePromise;
  const base = getApiOrigin();
  const url = `${base}/system/dict-data/list-simple?dictType=target_language`;
  languageCachePromise = fetch(url)
    .then((res) => res.json())
    .then((json) => {
      if (json?.code === 0 && Array.isArray(json.data)) {
        const map: Record<string, string> = {};
        for (const row of json.data) {
          const code = String(row.value ?? '').trim();
          if (code) map[code] = String(row.name ?? code).trim() || code;
        }
        languageCache = map;
        return map;
      }
      return {};
    })
    .catch(() => {
      languageCache = {};
      return {};
    });
  return languageCachePromise;
};

// 模块加载时立即预取
fetchLanguageCache();

/** 当缓存更新时通知依赖它的 React 组件重新渲染 */
type Listener = () => void;
const listeners = new Set<Listener>();
export const onLanguageCacheReady = (cb: Listener) => {
  listeners.add(cb);
  return () => { listeners.has(cb) && listeners.delete(cb); };
};
// 缓存就绪后通知所有订阅者
fetchLanguageCache().then(() => listeners.forEach((cb) => cb()));

/**
 * 语言代码→中文名称
 * 优先用调用方传入的 langRecord；未传时读模块级缓存（接口获取）
 * React 组件首次渲染时可能拿到 code 原值，可用 onLanguageCacheReady 订阅更新触发重渲染
 */
export const getLanguageName = (langCode: string, langRecord?: Record<string, string>): string => {
  if (!langCode) return langCode;
  if (langRecord) return langRecord[langCode] || langCode;
  if (languageCache) return languageCache[langCode] || langCode;
  fetchLanguageCache();
  return langCode;
};

/** 语种代码在自身语言环境下的展示名（如 en → English），失败时回退为大写 code */
export const getLanguageNativeDisplay = (langCode: string): string => {
  const code = String(langCode || "").trim();
  if (!code) return "";
  try {
    const self = new Intl.DisplayNames([code], { type: "language" }).of(code);
    if (self) return self;
  } catch {
    /* ignore */
  }
  try {
    return new Intl.DisplayNames(["en"], { type: "language" }).of(code) || code.toUpperCase();
  } catch {
    return code.toUpperCase();
  }
};

/**
 * 源语言下拉框统一数据（新建剧集等弹窗用，改此处则所有源语言选项同步）
 */
export interface SourceLanguageOption {
  value: string;
  label: string;
}

export const getSourceLanguageOptions = (): SourceLanguageOption[] => {
  // 与 getLanguageCodeFromName 的展示名一致；提交时由 Home / 快译创建 等转为 sourceLangCode（zh / en）
  return [
    { value: "中文", label: "中文" },
    { value: "英语", label: "英语" },
  ];
};

/**
 * 检查语言代码是否支持
 * @param langCode 语言代码
 * @returns 是否支持该语言代码
 */
export const isLanguageSupported = (langCode: string, langRecord?: Record<string, string>): boolean => {
  if (!langRecord) return false;
  return langCode in langRecord;
};

/**
 * 将源语言中文名称转换为语言代码（仅用于源语言下拉框解析，源语言只有中文/英语）
 * 目标语言的名称→代码映射由接口数据 + langRecord 反查完成，不再需要硬编码
 */
export const getLanguageCodeFromName = (languageName: string): string => {
  switch (languageName) {
    case '中文':
      return 'zh';
    case '英文':
    case '英语':
      return 'en';
    default:
      return '';
  }
};

/**
 * 判断目标语种是否与源语种相同，用于在「新增目标语」列表中隐藏
 * source 可为接口/langCode 的 ISO code（如 fr、zh）或中文名（如 法语、中文）
 * @param langRecord 可选，接口返回的语言映射 { code: label }；传入时优先用它做名称反查，无需维护硬编码 switch
 */
export const isLanguageExcludedAsTargetWhenSourceIs = (
  targetCode: string,
  targetDisplayName: string,
  sourceLangCodeOrName: string | null | undefined,
  langRecord?: Record<string, string>
): boolean => {
  const src = (sourceLangCodeOrName || "").trim();
  if (!src) return false;

  const langs = langRecord ?? {};
  const srcAsKnownCode = Object.keys(langs).find((k) => k.toLowerCase() === src.toLowerCase());
  const srcResolved = srcAsKnownCode ?? getLanguageCodeFromName(src);

  const code = String(targetCode || "").trim();
  const name = String(targetDisplayName || "").trim();

  if (code) {
    if (code.toLowerCase() === srcResolved.toLowerCase()) return true;
    if (code.toLowerCase() === src.toLowerCase()) return true;
  }
  if (name) {
    if (name === src) return true;
    // 优先用传入的 langRecord 做名称反查（如 name="泰语" → 在 { th: "泰语" } 中找到 key "th"）
    const nameAsCode = langRecord
      ? Object.keys(langRecord).find(k => langRecord[k] === name)
      : undefined;
    if (nameAsCode) {
      if (nameAsCode.toLowerCase() === srcResolved.toLowerCase()) return true;
    } else {
      // fallback 到静态 switch（兼容未传 langRecord 的场景）
      if (getLanguageCodeFromName(name) === srcResolved) return true;
    }
  }
  return false;
};
